import { useMemo, useState } from 'react'
import type { Category, Recipe } from '../../lib/supabase'
import { coverModel, matchesSearch } from '../../lib/recipeBook'
import { SheetPanel } from '../SheetPanel'
import { StarIcon } from '../icons'
import { SearchField } from '../ui/SearchField'
import { Highlight } from '../ui/Highlight'
import { Cover } from '../recipes/Cover'

// "+ Add meal" (feature spec 03): pick a recipe for a day. Starred first, then
// newest; search as on the Recipes tab. The title, day and search stay put and
// only the list scrolls, so with the keyboard up (SheetPanel sits the sheet on
// it) the field is still where it was.
export function RecipePicker({ dayLabel, recipes, categories, categoryFor, tintFor, onPick, onClose }: {
  dayLabel: string
  recipes: Recipe[]
  categories: Category[]
  categoryFor: (name: string) => string | null
  tintFor: (categoryId: string | null) => string
  onPick: (recipe: Recipe) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const shown = useMemo(() => recipes
    .filter(r => matchesSearch(r, query))
    .sort((a, b) => Number(b.starred) - Number(a.starred) || b.created_at.localeCompare(a.created_at)), [recipes, query])

  return (
    <SheetPanel onClose={onClose} maxHeight="85dvh" layout="column" ariaLabel={`Add a meal to ${dayLabel}`}>
      <div className="ml-picker">
        <header className="ml-picker__head">
          <h2 className="ml-display ml-picker__title">Add a meal</h2>
          <button type="button" className="ml-btn ml-btn--secondary ml-picker__close" onClick={onClose}>Close</button>
        </header>
        <p className="ml-label ml-picker__day">{dayLabel}</p>
        <SearchField label="Search recipes, ingredients or tags" value={query} onChange={setQuery} autoFocus />
        {shown.length === 0 && <p className="ml-caption">No recipes match</p>}
        <ul className="ml-picker__list">
          {shown.map(r => (
            <li key={r.id}>
              <button type="button" className="ml-picker__row" aria-label={`Add ${r.title}`} onClick={() => onPick(r)}>
                <Cover cover={coverModel(r, categoryFor, categories)} tintFor={tintFor} size="mini" />
                <span className="ml-picker__text">
                  <span className="ml-picker__name"><Highlight text={r.title} query={query} /></span>
                  <span className="ml-picker__meta">
                    {[r.minutes != null ? `${r.minutes} min` : null, r.servings != null ? `${r.servings} servings` : null].filter(Boolean).join(', ')}
                  </span>
                </span>
                {r.starred && <StarIcon size={16} filled />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </SheetPanel>
  )
}
